// User data sync - Firestore cloud storage for signed-in users
// Port of public/user-data.js with npm imports

import { db, auth } from './firebase.js';
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import {
  RECENT_BINDERS_KEY,
  MAX_RECENT_BINDERS,
  MY_BINDERS_KEY,
  MAX_MY_BINDERS,
} from '../logic/config.js';
import { getStorageItem, setStorageItem } from './storage-manager.js';
import { isUserAnonymous, initializeAuth } from '../stores/auth.svelte.js';
import {
  mergeBinders as mergeBindersLogic,
  getExchangeProgress,
  mergeMyBinders,
} from '../logic/user-data-logic.js';

// --- Local storage helpers ---

function getLocalRecentBinders() {
  try {
    const binders = getStorageItem(RECENT_BINDERS_KEY);
    if (!binders) return [];
    if (Array.isArray(binders) && binders.every((b) => typeof b === 'object' && b.url && b.name)) {
      return binders;
    }
    return [];
  } catch (e) {
    console.warn('[UserData] Failed to read local binders:', e);
    return [];
  }
}

function setLocalRecentBinders(binders) {
  try {
    setStorageItem(RECENT_BINDERS_KEY, binders);
  } catch (e) {
    console.warn('[UserData] Failed to save local binders:', e);
  }
}

// --- Firestore helpers ---

function getUserDocRef() {
  const user = auth.currentUser;
  if (!user) return null;
  return doc(db, 'users', user.uid);
}

// --- Recent binders (cloud) ---

export async function loadRecentBindersFromCloud() {
  if (isUserAnonymous()) return null;
  const userDocRef = getUserDocRef();
  if (!userDocRef) return null;

  try {
    const docSnap = await getDoc(userDocRef);
    if (docSnap.exists()) {
      return docSnap.data().recentBinders || [];
    }
    return [];
  } catch (error) {
    console.error('[UserData] Failed to load binders from cloud:', error);
    return null;
  }
}

export async function saveRecentBindersToCloud(binders) {
  if (isUserAnonymous()) return false;
  const userDocRef = getUserDocRef();
  if (!userDocRef) return false;

  try {
    await setDoc(
      userDocRef,
      {
        recentBinders: binders.slice(0, MAX_RECENT_BINDERS),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    return true;
  } catch (error) {
    console.error('[UserData] Failed to save binders to cloud:', error);
    return false;
  }
}

// --- Merge & sync ---

function mergeBinders(localBinders, cloudBinders) {
  return mergeBindersLogic(localBinders, cloudBinders, MAX_RECENT_BINDERS);
}

export async function syncRecentBinders() {
  if (isUserAnonymous()) {
    console.log('[UserData] User is anonymous, skipping sync');
    return;
  }

  const localBinders = getLocalRecentBinders();
  if (localBinders.length === 0) {
    // Nothing to merge, and the home page reads the cloud list right after:
    // skip a Firestore round trip on every signed-in page load.
    return;
  }

  console.log('[UserData] Syncing recent binders...');
  const cloudBinders = await loadRecentBindersFromCloud();

  if (cloudBinders === null) {
    console.log('[UserData] Could not load cloud binders');
    return;
  }

  if (cloudBinders.length === 0) {
    console.log('[UserData] First sync - uploading local binders to cloud');
    await saveRecentBindersToCloud(localBinders.slice(0, MAX_RECENT_BINDERS));
  } else {
    const mergedBinders = mergeBinders(localBinders, cloudBinders);
    if (mergedBinders.length !== cloudBinders.length) {
      await saveRecentBindersToCloud(mergedBinders);
    }
  }

  setLocalRecentBinders([]);
  console.log('[UserData] Synced (cloud is source of truth, local cleared)');
}

export async function saveBindersToCloud(binders) {
  if (isUserAnonymous()) return;
  await saveRecentBindersToCloud(binders);
}

// --- Want list (deck-designated) retired (ADR-0009) ---
// The deck want-list (getWantList/saveWantList/syncWantList + the Firestore
// `wantListMeta` field and want_list_api sync) is replaced by the explicit,
// user-managed Want List. Entry management now lives in wantlist-api.js and the
// /u/@handle/wantlist page; the in-binder star adds/removes entries. The legacy
// Firestore `wantListMeta` field never had a code path in this file (already
// gone by the time of this comment); the deck `want_list_api` endpoint was
// dropped in migration 032.

// --- My binders sync ---

export async function syncMyBinders() {
  if (isUserAnonymous()) return;

  console.log('[UserData] Syncing my binders...');

  let localMyBinders = [];
  try {
    const raw = getStorageItem(MY_BINDERS_KEY);
    if (Array.isArray(raw)) localMyBinders = raw;
  } catch {
    // ignore
  }

  if (localMyBinders.length === 0) {
    console.log('[UserData] No local my binders to sync');
    return;
  }

  try {
    const { getClaimedBinders, saveClaimedBinders } = await import('./groups-api.js');
    const cloudMyBinders = await getClaimedBinders();

    if (cloudMyBinders.length === 0 && localMyBinders.length > 0) {
      console.log('[UserData] First sync - uploading local my binders to cloud');
      await saveClaimedBinders(localMyBinders.slice(0, MAX_MY_BINDERS));
    } else if (localMyBinders.length > 0) {
      const merged = mergeMyBinders(localMyBinders, cloudMyBinders, MAX_MY_BINDERS);
      if (merged.length !== cloudMyBinders.length) {
        await saveClaimedBinders(merged);
      }
    }
  } catch (e) {
    console.warn('[UserData] Failed to sync my binders:', e);
    return;
  }

  setStorageItem(MY_BINDERS_KEY, null);
  console.log('[UserData] My binders synced (cloud is source of truth)');
}

// --- Trade queries ---

// getExchangeProgress imported from user-data-logic.js

async function ensureAuth() {
  let user = auth.currentUser;
  if (!user) {
    try {
      await initializeAuth();
      user = auth.currentUser;
    } catch {
      return null;
    }
  }
  return user;
}

export async function getOpenTradesForUser() {
  const user = await ensureAuth();
  if (!user) return [];

  try {
    const tradesQuery = query(
      collection(db, 'trades'),
      where('allowedTokens', 'array-contains', user.uid)
    );
    const snapshot = await getDocs(tradesQuery);
    const trades = [];

    for (const tradeDoc of snapshot.docs) {
      const data = tradeDoc.data();
      if (data.status === 'cancelled' || data.cancelledBy || data.status === 'complete') continue;
      if (data.acceptedByA && data.acceptedByB) continue;

      const userIsA = data.sideA?.userToken === user.uid;
      const userIsB = data.sideB?.userToken === user.uid;
      if (!userIsA && !userIsB) continue;

      const otherSide = userIsA ? data.sideB : data.sideA;
      const userSide = userIsA ? data.sideA : data.sideB;

      const userCards = (userSide?.cards || []).filter((c) => (c.status || 'trade') === 'trade');
      const otherCards = (otherSide?.cards || []).filter((c) => (c.status || 'trade') === 'trade');
      const totalCards =
        userCards.reduce((sum, c) => sum + (c.quantity || 1), 0) +
        otherCards.reduce((sum, c) => sum + (c.quantity || 1), 0);
      if (totalCards === 0) continue;

      trades.push({
        tradeId: tradeDoc.id,
        binderUrl: otherSide?.binderUrl || '',
        binderName: otherSide?.binderName || 'Unknown',
        ownerName: otherSide?.ownerName || null,
        userBinderUrl: userSide?.binderUrl || '',
        userSide: userIsA ? 'A' : 'B',
        updatedAt: data.updatedAt?.toDate?.() || new Date(),
        createdAt: data.createdAt?.toDate?.() || new Date(),
      });
    }

    trades.sort((a, b) => b.updatedAt - a.updatedAt);
    return trades;
  } catch (error) {
    console.error('[UserData] Failed to fetch open trades:', error);
    return [];
  }
}

export async function getAwaitingExchangeTrades() {
  const user = await ensureAuth();
  if (!user) return [];

  try {
    const tradesQuery = query(
      collection(db, 'trades'),
      where('allowedTokens', 'array-contains', user.uid)
    );
    const snapshot = await getDocs(tradesQuery);
    const trades = [];

    for (const tradeDoc of snapshot.docs) {
      const data = tradeDoc.data();
      const isFinalized = data.acceptedByA && data.acceptedByB;
      const isCancelled = data.status === 'cancelled' || data.cancelledBy;
      const isComplete = data.status === 'complete';
      if (!isFinalized || isCancelled || isComplete) continue;

      const userIsA = data.sideA?.userToken === user.uid;
      const userIsB = data.sideB?.userToken === user.uid;
      if (!userIsA && !userIsB) continue;

      const oppositeSide = userIsA ? data.sideB : data.sideA;
      const userSide = userIsA ? data.sideA : data.sideB;

      const userCards = (userSide?.cards || []).filter((c) => (c.status || 'trade') === 'trade');
      const otherCards = (oppositeSide?.cards || []).filter(
        (c) => (c.status || 'trade') === 'trade'
      );
      const totalCards =
        userCards.reduce((sum, c) => sum + (c.quantity || 1), 0) +
        otherCards.reduce((sum, c) => sum + (c.quantity || 1), 0);
      if (totalCards === 0) continue;

      const progress = getExchangeProgress(oppositeSide?.cards);
      if (progress.complete) continue;

      trades.push({
        tradeId: tradeDoc.id,
        binderUrl: oppositeSide?.binderUrl || '',
        binderName: oppositeSide?.binderName || 'Unknown',
        ownerName: oppositeSide?.ownerName || null,
        userBinderUrl: userSide?.binderUrl || '',
        userSide: userIsA ? 'A' : 'B',
        exchangeProgress: progress,
        updatedAt: data.updatedAt?.toDate?.() || new Date(),
        createdAt: data.createdAt?.toDate?.() || new Date(),
      });
    }

    trades.sort((a, b) => b.updatedAt - a.updatedAt);
    return trades;
  } catch (error) {
    console.error('[UserData] Failed to fetch awaiting exchange trades:', error);
    return [];
  }
}

/**
 * Fetch open and awaiting-exchange trades in a single Firestore query.
 * Returns { openTrades, awaitingTrades }.
 */
export async function getActiveTrades() {
  const user = await ensureAuth();
  if (!user) return { openTrades: [], awaitingTrades: [] };

  try {
    const tradesQuery = query(
      collection(db, 'trades'),
      where('allowedTokens', 'array-contains', user.uid)
    );
    const snapshot = await getDocs(tradesQuery);
    const openTrades = [];
    const awaitingTrades = [];

    for (const tradeDoc of snapshot.docs) {
      const data = tradeDoc.data();
      const isCancelled = data.status === 'cancelled' || data.cancelledBy;
      const isComplete = data.status === 'complete';
      if (isCancelled || isComplete) continue;

      const userIsA = data.sideA?.userToken === user.uid;
      const userIsB = data.sideB?.userToken === user.uid;
      if (!userIsA && !userIsB) continue;

      const isFinalized = data.acceptedByA && data.acceptedByB;
      const otherSide = userIsA ? data.sideB : data.sideA;
      const userSide = userIsA ? data.sideA : data.sideB;

      const userCards = (userSide?.cards || []).filter((c) => (c.status || 'trade') === 'trade');
      const otherCards = (otherSide?.cards || []).filter((c) => (c.status || 'trade') === 'trade');
      const totalCards =
        userCards.reduce((sum, c) => sum + (c.quantity || 1), 0) +
        otherCards.reduce((sum, c) => sum + (c.quantity || 1), 0);
      if (totalCards === 0) continue;

      const base = {
        tradeId: tradeDoc.id,
        binderUrl: otherSide?.binderUrl || '',
        binderName: otherSide?.binderName || 'Unknown',
        ownerName: otherSide?.ownerName || null,
        userBinderUrl: userSide?.binderUrl || '',
        userSide: userIsA ? 'A' : 'B',
        updatedAt: data.updatedAt?.toDate?.() || new Date(),
        createdAt: data.createdAt?.toDate?.() || new Date(),
      };

      if (isFinalized) {
        const progress = getExchangeProgress(otherSide?.cards);
        if (!progress.complete) {
          awaitingTrades.push({ ...base, exchangeProgress: progress });
        }
      } else {
        openTrades.push(base);
      }
    }

    openTrades.sort((a, b) => b.updatedAt - a.updatedAt);
    awaitingTrades.sort((a, b) => b.updatedAt - a.updatedAt);
    return { openTrades, awaitingTrades };
  } catch (error) {
    console.error('[UserData] Failed to fetch active trades:', error);
    return { openTrades: [], awaitingTrades: [] };
  }
}

export async function getAllTradesForUser() {
  const user = await ensureAuth();
  if (!user) return [];

  try {
    const tradesQuery = query(
      collection(db, 'trades'),
      where('allowedTokens', 'array-contains', user.uid)
    );
    const snapshot = await getDocs(tradesQuery);
    const trades = [];

    for (const tradeDoc of snapshot.docs) {
      const data = tradeDoc.data();
      const userIsA = data.sideA?.userToken === user.uid;
      const userIsB = data.sideB?.userToken === user.uid;
      if (!userIsA && !userIsB) continue;

      const otherSide = userIsA ? data.sideB : data.sideA;
      const userSide = userIsA ? data.sideA : data.sideB;

      const isCancelled = data.status === 'cancelled' || data.cancelledBy;
      const isComplete = data.status === 'complete';
      const isFinalized = data.acceptedByA && data.acceptedByB;

      let tradeState;
      if (isCancelled) tradeState = 'cancelled';
      else if (isComplete) tradeState = 'complete';
      else if (isFinalized) tradeState = 'awaiting';
      else tradeState = 'in-progress';

      const userCards = (userSide?.cards || []).filter((c) => (c.status || 'trade') === 'trade');
      const otherCards = (otherSide?.cards || []).filter((c) => (c.status || 'trade') === 'trade');
      const userValue = userCards.reduce(
        (sum, c) => sum + (c.priceUsd || 0) * (c.quantity || 1),
        0
      );
      const otherValue = otherCards.reduce(
        (sum, c) => sum + (c.priceUsd || 0) * (c.quantity || 1),
        0
      );
      const userCardCount = userCards.reduce((sum, c) => sum + (c.quantity || 1), 0);
      const otherCardCount = otherCards.reduce((sum, c) => sum + (c.quantity || 1), 0);
      if (userCardCount + otherCardCount === 0) continue;

      trades.push({
        tradeId: tradeDoc.id,
        state: tradeState,
        cancelledBy: data.cancelledBy === user.uid ? 'you' : data.cancelledBy ? 'them' : null,
        otherBinderUrl: otherSide?.binderUrl || '',
        otherBinderName: otherSide?.binderName || 'Unknown',
        otherOwnerName: otherSide?.ownerName || null,
        userBinderUrl: userSide?.binderUrl || '',
        userCardCount,
        otherCardCount,
        userValue,
        otherValue,
        userCards,
        otherCards,
        userSide: userIsA ? 'A' : 'B',
        otherSide: userIsA ? 'B' : 'A',
        updatedAt: data.updatedAt?.toDate?.() || new Date(),
        createdAt: data.createdAt?.toDate?.() || new Date(),
        cancelledAt: data.cancelledAt?.toDate?.() || null,
      });
    }

    trades.sort((a, b) => b.updatedAt - a.updatedAt);
    return trades;
  } catch (error) {
    console.error('[UserData] Failed to fetch all trades:', error);
    return [];
  }
}
